import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useRef } from "react";
const SLOT_MINUTES = 30;
// Snap drag drops to this finer grid (matches the 15-min `step="900"` we use
// on time inputs). Lets the user nudge blocks by quarters without forcing the
// row layout to be 15-min throughout.
const DRAG_SNAP_MINUTES = 15;
const DEFAULT_SLOT_PX = 44;
function parseHHMMSS(s) {
    const [h, m] = s.split(":").map(Number);
    return { h: h || 0, m: m || 0 };
}
// 24h hour-of-day -> "9 AM" / "12 PM" / "1 PM" etc.
function fmtHourLabel(hour24) {
    if (hour24 === 0)
        return "12 AM";
    if (hour24 === 12)
        return "12 PM";
    if (hour24 < 12)
        return `${hour24} AM`;
    return `${hour24 - 12} PM`;
}
// Force AM/PM for any Date — `toLocaleTimeString` honors locale, but we want AM/PM
// consistently across browsers/locales for the schedule.
export function fmtBlockTime(d) {
    return d.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    });
}
function ymdLocal(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
// Local-date key for an ISO timestamp — parses, then formats in browser local tz.
// IMPORTANT: don't use `iso.slice(0, 10)`, which gives the UTC date and misbuckets
// blocks created near midnight (e.g., 9 PM EDT = 1 AM UTC the next day).
function isoToLocalYmd(iso) {
    return ymdLocal(new Date(iso));
}
export default function CalendarGrid({ date, awakeStart, awakeEnd, blocks, onCreate, onSelect, onMove, onProgressPrompt, nowTick, slotPx = DEFAULT_SLOT_PX, }) {
    const { startMin, endMin, slots } = useMemo(() => {
        const a = parseHHMMSS(awakeStart);
        const b = parseHHMMSS(awakeEnd);
        const startM = a.h * 60 + a.m;
        const endM = b.h * 60 + b.m;
        const slotCount = Math.max(1, Math.floor((endM - startM) / SLOT_MINUTES));
        const slotArr = Array.from({ length: slotCount }, (_, i) => startM + i * SLOT_MINUTES);
        return { startMin: startM, endMin: endM, slots: slotArr };
    }, [awakeStart, awakeEnd]);
    const gridHeightPx = ((endMin - startMin) / SLOT_MINUTES) * slotPx;
    // "Now" line — top offset in px, or null if today doesn't equal `date` or if
    // the current time is outside the awake window (no point in drawing it). We
    // recompute on each render; the parent ticks state every 5 minutes via
    // `nowTick` so the line drifts down through the day without a hard refresh.
    const nowLineTop = useMemo(() => {
        // nowTick is referenced so React's exhaustive-deps doesn't warn — value
        // itself is unused (we just need to recompute when the parent ticks).
        void nowTick;
        const now = new Date();
        if (ymdLocal(now) !== ymdLocal(date))
            return null;
        const nowMin = now.getHours() * 60 + now.getMinutes();
        if (nowMin < startMin || nowMin > endMin)
            return null;
        return ((nowMin - startMin) / SLOT_MINUTES) * slotPx;
    }, [date, startMin, endMin, slotPx, nowTick]);
    const nowLineLabel = useMemo(() => {
        const d = new Date();
        return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
        // recompute alongside the line position
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nowTick]);
    // Drag state — kept in refs (not state) so it doesn't trigger re-renders
    // mid-drag. The browser already gives us hover feedback via the cursor.
    const gridRef = useRef(null);
    // Where the user "grabbed" the block (offset from block's top edge in px).
    // Subtracted from the drop y so the block lands proportionally where it
    // was picked up, not snapped to the cursor.
    const dragOffsetYRef = useRef(0);
    const draggedBlockRef = useRef(null);
    const dayBlocks = useMemo(() => {
        const dayStr = ymdLocal(date);
        return blocks
            .filter((b) => isoToLocalYmd(b.start_at) === dayStr)
            .map((b) => {
            const s = new Date(b.start_at);
            const e = new Date(b.end_at);
            const sMin = s.getHours() * 60 + s.getMinutes();
            const eMin = e.getHours() * 60 + e.getMinutes();
            // Map block time to grid pixels; clamp to the awake window so blocks at
            // the edges don't render off-grid and stay aligned with the slot rows.
            const rawTop = ((sMin - startMin) / SLOT_MINUTES) * slotPx;
            const rawBottom = ((eMin - startMin) / SLOT_MINUTES) * slotPx;
            const top = Math.max(0, rawTop);
            const bottom = Math.min(gridHeightPx, rawBottom);
            const height = Math.max(slotPx, bottom - top); // never thinner than one 30-min slot
            return { block: b, top, height, rawTop, rawBottom };
        })
            .filter((x) => x.rawBottom > 0 && x.rawTop < gridHeightPx);
    }, [blocks, date, startMin, gridHeightPx, slotPx]);
    function handleSlotClick(slotMin) {
        const start = new Date(date);
        start.setHours(Math.floor(slotMin / 60), slotMin % 60, 0, 0);
        // SchedulePanel drop mode — when it sets the window flag, clicking
        // an empty slot routes through the "schedule:drop" event instead of
        // opening the BlockEditor for a fresh block. The panel handles the
        // POST with its current task + duration.
        if (window.__schedulePanelOpen) {
            window.dispatchEvent(new CustomEvent("schedule:drop", {
                detail: { start_at: start.toISOString() },
            }));
            return;
        }
        if (!onCreate)
            return;
        const end = new Date(start);
        end.setMinutes(start.getMinutes() + SLOT_MINUTES);
        onCreate(start, end);
    }
    // Translate a drop y-coord (relative to the grid container) into a wall-clock
    // start time on `date`, snapped to DRAG_SNAP_MINUTES. Subtracts the drag
    // offset so the block lands where you grabbed it from.
    function dropYToStartTime(clientY, gridRect) {
        const yInGrid = clientY - gridRect.top - dragOffsetYRef.current;
        // pixels → grid minutes from awake-start, then snap to the 15-min grid.
        const rawMinutes = (yInGrid / slotPx) * SLOT_MINUTES;
        const minutesFromStart = Math.round(rawMinutes / DRAG_SNAP_MINUTES) * DRAG_SNAP_MINUTES;
        const absoluteMin = startMin + minutesFromStart;
        // Clamp so we don't end up before midnight or after the awake window.
        const clamped = Math.max(0, Math.min(absoluteMin, 24 * 60 - SLOT_MINUTES));
        const start = new Date(date);
        start.setHours(Math.floor(clamped / 60), clamped % 60, 0, 0);
        return start;
    }
    function onBlockDragStart(e, block) {
        if (!onMove)
            return;
        draggedBlockRef.current = block;
        // Pixel offset within the block where the grab started — used so the
        // drop position lines up with where the user was holding.
        const rect = e.currentTarget.getBoundingClientRect();
        dragOffsetYRef.current = e.clientY - rect.top;
        // Required to enable drag in Firefox; payload doesn't matter to us.
        try {
            e.dataTransfer.setData("text/plain", block.id);
            e.dataTransfer.effectAllowed = "move";
        }
        catch {
            /* Some browsers throw if setData is called without a real source — fine */
        }
        e.currentTarget.classList.add("dragging");
    }
    function onBlockDragEnd(e) {
        e.currentTarget.classList.remove("dragging");
        draggedBlockRef.current = null;
        dragOffsetYRef.current = 0;
    }
    function onGridDragOver(e) {
        if (!onMove || !draggedBlockRef.current)
            return;
        // preventDefault is required to allow dropping; without it the browser
        // rejects the drop and the cursor shows "not allowed".
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    }
    function onGridDrop(e) {
        if (!onMove)
            return;
        const block = draggedBlockRef.current;
        if (!block || !gridRef.current)
            return;
        e.preventDefault();
        const rect = gridRef.current.getBoundingClientRect();
        const newStart = dropYToStartTime(e.clientY, rect);
        onMove(block, newStart);
    }
    return (_jsxs("div", { ref: gridRef, className: "calendar-grid", style: { height: slots.length * slotPx }, onDragOver: onGridDragOver, onDrop: onGridDrop, children: [_jsx("div", { className: "grid-rows", children: slots.map((m, i) => {
                    const isHourMark = m % 60 === 0;
                    return (_jsxs("div", { className: `grid-row ${isHourMark ? "hour-mark" : ""}`, style: { height: slotPx }, onClick: () => handleSlotClick(m), children: [_jsx("div", { className: "time-label", children: isHourMark ? fmtHourLabel(Math.floor(m / 60)) : "" }), _jsx("div", { className: "slot-fill" })] }, i));
                }) }), _jsx("div", { className: "grid-blocks", children: dayBlocks.map(({ block, top, height }) => {
                    // Show the "Complete?" button on EVERY past block — linked or not.
                    // Linked blocks open the task progress slider; free-form blocks
                    // toggle block.is_completed in the same modal. Personal blocks +
                    // recurring meeting blocks still show it (the user picks if it was
                    // done). The end-time check is "block ended at or before now".
                    const ended = new Date(block.end_at).getTime() <= Date.now();
                    // Already-marked-completed blocks already render with the
                    // "block-completed" line-through styling — no need for the prompt.
                    // reviewed_at = the user opened the Complete? prompt and saved
                    // *Not yet* progress; the block is handled even though the
                    // underlying task isn't 100%, so we drop the prompt and dim
                    // the tile the same way completed blocks render.
                    const reviewed = block.reviewed_at != null;
                    const showProgressBtn = ended && onProgressPrompt && !block.is_completed && !reviewed;
                    return (_jsxs("div", { className: `block block-${block.block_type} ${block.is_personal ? "block-personal" : ""} ${block.redacted ? "block-redacted" : ""} ${block.is_completed ? "block-completed" : ""} ${reviewed ? "block-reviewed" : ""}`, style: { top, height }, 
                        // HTML5 DnD only fires the lifecycle when draggable=true. Gating
                        // on `onMove` keeps blocks static if the parent hasn't wired it up.
                        draggable: Boolean(onMove), onDragStart: (e) => onBlockDragStart(e, block), onDragEnd: onBlockDragEnd, onClick: () => onSelect?.(block), title: onMove
                            ? `${block.title}\nDrag to reschedule, click to edit`
                            : block.title, children: [_jsxs("div", { className: "block-title-row", children: [_jsx("div", { className: "block-title", children: block.title }), _jsxs("div", { className: "block-badges", children: [block.recurring_pattern_id && (_jsx("span", { className: "block-badge", title: "Recurring meeting", children: "\u21BB" })), block.is_completed && (_jsx("span", { className: "block-badge block-badge-done", title: "Marked complete", children: "\u2713" }))] })] }), _jsxs("div", { className: "block-time", children: [fmtBlockTime(new Date(block.start_at)), "\u2013", fmtBlockTime(new Date(block.end_at))] }), block.project_name && (_jsxs("div", { className: "block-project", title: block.project_name, children: [block.project_name, block.task_title && block.task_title !== block.title && (_jsxs("span", { className: "block-project-sub", children: [" ", "\u00B7 ", block.task_title] }))] })), block.checklist_item_title && (_jsxs("div", { className: "block-checklist", title: block.task_title
                                    ? `${block.task_title}: ${block.checklist_item_title}`
                                    : block.checklist_item_title, children: [block.task_title && (_jsxs("span", { className: "block-checklist-task", children: [block.task_title, ":", " "] })), _jsx("span", { className: "block-checklist-name", children: block.checklist_item_title }), block.checklist_position != null &&
                                        block.checklist_total != null && (_jsxs("span", { className: "block-checklist-pos", children: [" ", "(", block.checklist_position, " of", " ", block.checklist_total, ")"] })), block.sister_position != null &&
                                        block.sister_total != null &&
                                        block.sister_total > 1 && (_jsxs("span", { className: "block-sister-pill", title: `Sister block ${block.sister_position} of ${block.sister_total} for this estimate`, children: ["Sister ", block.sister_position, "/", block.sister_total] }))] })), (block.task_assignee_names?.length ?? 0) > 1 && (_jsxs("div", { className: "block-attendees", title: (block.task_assignee_names ?? []).join(", "), children: ["\uD83D\uDC65 ", (block.task_assignee_names ?? []).join(", ")] })), block.task_id && block.task_progress_percent != null && (_jsxs("div", { className: "block-progress-row", children: [_jsx("div", { className: "block-progress-track", children: _jsx("div", { className: "block-progress-fill", style: { width: `${block.task_progress_percent}%` } }) }), _jsxs("span", { className: "block-progress-pct", children: [block.task_progress_percent, "%"] })] })), showProgressBtn && (_jsx("button", { type: "button", className: "block-progress-btn", onClick: (e) => {
                                    // Don't open the regular block editor when clicking the
                                    // progress button — let the dedicated progress modal handle it.
                                    e.stopPropagation();
                                    onProgressPrompt(block);
                                }, title: "This block has ended \u2014 how far did you get on the linked task?", children: "Complete?" }))] }, block.id));
                }) }), nowLineTop !== null && (_jsx("div", { className: "now-line", style: { top: nowLineTop }, children: _jsxs("span", { className: "now-line-label", children: ["Now ", nowLineLabel] }) }))] }));
}
